import { create } from 'zustand';
import { api, OfflineError } from '../lib/api';
import type { CardTemplate, SentCard, Intensity, CreateCardTemplateRequest, UpdateCardTemplateRequest } from '@nudge/shared';
import { haptics } from '../lib/haptics';
import { useAuthStore } from './auth';

interface CardsState {
  templates: CardTemplate[];
  activeCards: SentCard[];
  badgeCount: number;
  fetchDeck: (circleId: string) => Promise<void>;
  fetchActive: (circleId: string) => Promise<void>;
  sendCard: (circleId: string, templateId: string, intensity: Intensity, expiresAt?: string) => Promise<SentCard | null>;
  clearCard: (id: string) => Promise<void>;
  ackCard: (id: string, response?: string) => Promise<void>;
  applyEvent: (event: { type: string;[k: string]: unknown }) => void;
  createTemplate: (req: CreateCardTemplateRequest) => Promise<CardTemplate>;
  updateTemplate: (id: string, req: UpdateCardTemplateRequest) => Promise<CardTemplate>;
  archiveTemplate: (id: string) => Promise<void>;
  reorderTemplates: (updates: Array<{ id: string; sort_order: number }>) => Promise<void>;
  toggleFavorite: (id: string) => Promise<void>;
  setBadge: (n: number) => void;
}

export const useCardsStore = create<CardsState>((set, get) => ({
  templates: [],
  activeCards: [],
  badgeCount: 0,

  async fetchDeck(circleId) {
    const { templates } = await api.getTemplates(circleId);
    set({ templates });
  },

  async fetchActive(circleId) {
    const { cards } = await api.getActive(circleId);
    set({ activeCards: cards });
  },

  async sendCard(circleId, templateId, intensity, expiresAt) {
    if (intensity === 'firm') haptics.firmSend();
    else haptics.softSend();
    try {
      const { sent_card } = await api.sendCard({
        card_template_id: templateId,
        circle_id: circleId,
        intensity,
        expires_at: expiresAt,
      });
      // first-card-sent flag for BMAC prompt
      const auth = useAuthStore.getState();
      if (auth.user && !auth.user.settings.first_card_at) {
        await api.updateMe({ settings: { first_card_at: new Date().toISOString() } });
        auth.setUser({ ...auth.user, settings: { ...auth.user.settings, first_card_at: new Date().toISOString() } });
      }
      // Optimistically add (server WS will also broadcast — applyEvent dedupes by id)
      set({ activeCards: [sent_card, ...get().activeCards.filter((c) => c.id !== sent_card.id)] });
      return sent_card;
    } catch (e) {
      if (e instanceof OfflineError) {
        // Optimistic queued send: surface in mySentCards via a synthetic placeholder
        const auth = useAuthStore.getState();
        const placeholder: SentCard = {
          id: `pending-${Date.now()}`,
          card_template_id: templateId,
          sender_id: auth.user?.id ?? '',
          circle_id: circleId,
          intensity,
          expires_at: expiresAt ?? null,
          cleared_at: null,
          created_at: new Date().toISOString(),
          template: get().templates.find((t) => t.id === templateId),
          sender: auth.user ? { id: auth.user.id, display_name: auth.user.display_name, avatar_url: auth.user.avatar_url } : undefined,
        };
        set({ activeCards: [placeholder, ...get().activeCards] });
        return placeholder;
      }
      throw e;
    }
  },

  async clearCard(id) {
    set({ activeCards: get().activeCards.filter((c) => c.id !== id) });
    try {
      await api.clearCard(id);
    } catch (e) {
      if (!(e instanceof OfflineError)) throw e;
    }
  },

  async ackCard(id, response) {
    haptics.ackTap();
    try {
      await api.ackCard(id, { response });
      // Optimistic local update
      const auth = useAuthStore.getState();
      const userId = auth.user?.id;
      if (!userId) return;
      set({
        activeCards: get().activeCards.map((c) => {
          if (c.id !== id) return c;
          const acks = c.acknowledgments ?? [];
          if (acks.find((a) => a.user_id === userId)) return c;
          return {
            ...c,
            acknowledgments: [
              ...acks,
              {
                id: `local-${Date.now()}`,
                sent_card_id: id,
                user_id: userId,
                response: response ?? null,
                created_at: new Date().toISOString(),
              },
            ],
          };
        }),
      });
    } catch (e) {
      if (!(e instanceof OfflineError)) throw e;
    }
  },

  applyEvent(event) {
    if (event.type === 'card_sent' && event.card) {
      const card = event.card as SentCard;
      set({
        activeCards: [card, ...get().activeCards.filter((c) => c.id !== card.id && !c.id.startsWith('pending-'))],
      });
    } else if (event.type === 'card_cleared' && event.sent_card_id) {
      set({ activeCards: get().activeCards.filter((c) => c.id !== event.sent_card_id) });
    } else if (event.type === 'card_acknowledged' && event.sent_card_id) {
      const targetId = event.sent_card_id as string;
      const userId = event.user_id as string;
      const response = (event.response as string | null) ?? null;
      set({
        activeCards: get().activeCards.map((c) => {
          if (c.id !== targetId) return c;
          const acks = (c.acknowledgments ?? []).filter((a) => a.user_id !== userId);
          return {
            ...c,
            acknowledgments: [...acks, {
              id: `srv-${Date.now()}`,
              sent_card_id: targetId,
              user_id: userId,
              response,
              created_at: new Date().toISOString(),
            }],
          };
        }),
      });
    }
  },

  async createTemplate(req) {
    const { template } = await api.createTemplate(req);
    set({ templates: [...get().templates, template] });
    return template;
  },

  async updateTemplate(id, req) {
    const { template } = await api.updateTemplate(id, req);
    set({ templates: get().templates.map((t) => (t.id === id ? template : t)) });
    return template;
  },

  async archiveTemplate(id) {
    await api.deleteTemplate(id);
    set({ templates: get().templates.filter((t) => t.id !== id) });
  },

  async reorderTemplates(updates) {
    await api.reorderTemplates(updates);
    const map = new Map(updates.map((u) => [u.id, u.sort_order]));
    set({
      templates: get().templates
        .map((t) => (map.has(t.id) ? { ...t, sort_order: map.get(t.id)! } : t))
        .sort((a, b) => a.sort_order - b.sort_order),
    });
  },

  async toggleFavorite(id) {
    const current = get().templates.find((t) => t.id === id);
    if (!current) return;
    const next = !current.is_favorite;
    set({ templates: get().templates.map((t) => (t.id === id ? { ...t, is_favorite: next } : t)) });
    try {
      if (next) await api.favoriteTemplate(id);
      else await api.unfavoriteTemplate(id);
    } catch (e) {
      set({ templates: get().templates.map((t) => (t.id === id ? { ...t, is_favorite: !next } : t)) });
      throw e;
    }
  },

  setBadge(n: number) {
    set({ badgeCount: n });
    if (typeof navigator !== 'undefined') {
      const setAppBadge = (navigator as Navigator & { setAppBadge?: (n: number) => void }).setAppBadge;
      const clearAppBadge = (navigator as Navigator & { clearAppBadge?: () => void }).clearAppBadge;
      try {
        if (n > 0 && typeof setAppBadge === 'function') setAppBadge(n);
        else if (n === 0 && typeof clearAppBadge === 'function') clearAppBadge();
      } catch { /* ignore */ }
    }
  },
}));
